"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CurrentChildrenProvider = exports.CurrentChildrenContext = void 0;
const tslib_1 = require("tslib");
const client_utils_1 = require("@sensenet/client-utils");
const react_1 = tslib_1.__importStar(require("react"));
const hooks_1 = require("../hooks");
const current_content_1 = require("./current-content");
const load_settings_1 = require("./load-settings");
/**
 * Context that will return with a list of a current content's children
 */
exports.CurrentChildrenContext = react_1.default.createContext([]);
exports.CurrentChildrenContext.displayName = 'CurrentChildrenContext';
/**
 * Provider component for the CurrentChildrenContext component
 * Loads the children of the current content.
 * Loads an ancestor list from the Repository. Has to be wrapped with a **CurrentContentContext** and a **RepositoryContext**
 */
const CurrentChildrenProvider = (props) => {
    const currentContent = react_1.useContext(current_content_1.CurrentContentContext);
    const [children, setChildren] = react_1.useState([]);
    const alwaysRefresh = props.alwaysRefresh || currentContent.Type === 'SmartFolder';
    const [reloadToken, setReloadToken] = react_1.useState(1);
    const repo = hooks_1.useRepository();
    const eventHub = hooks_1.useRepositoryEvents();
    const loadSettings = react_1.useContext(load_settings_1.LoadSettingsContext);
    const requestReload = () => setReloadToken(Math.random());
    const [error, setError] = react_1.useState();
    react_1.useEffect(() => {
        const ac = new AbortController();
        (() => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
            if (currentContent.Path) {
                try {
                    const childrenResult = yield repo.loadCollection({
                        path: currentContent.Path,
                        requestInit: { signal: ac.signal },
                        oDataOptions: client_utils_1.deepMerge(loadSettings.loadChildrenSettings, props.loadSettings),
                    });
                    setChildren(childrenResult.d.results);
                }
                catch (err) {
                    if (!ac.signal.aborted) {
                        setError(err);
                    }
                }
            }
        }))();
        return () => ac.abort();
    }, [currentContent.Path, loadSettings.loadChildrenSettings, props.loadSettings, repo, reloadToken]);
    react_1.useEffect(() => {
        const handleCreate = (contents) => {
            if (alwaysRefresh ||
                contents.some((createdContent) => createdContent.ParentId === currentContent.Id ||
                    client_utils_1.PathHelper.isAncestorOf(currentContent.Path, createdContent.Path))) {
                requestReload();
            }
        };
        const subscriptions = [
            eventHub.onCustomActionExecuted.subscribe((event) => {
                if (event.actionOptions.method !== 'GET') {
                    switch (event.actionOptions.name) {
                        case 'DeleteBatch':
                        case 'MoveBatch':
                        case 'CopyBatch':
                        case 'PreviewAvailable':
                        case 'RegeneratePreviews':
                        case 'GetExistingPreviewImages':
                            return;
                        case 'Restore':
                            if (alwaysRefresh ||
                                client_utils_1.PathHelper.getParentPath(event.actionOptions.idOrPath) === client_utils_1.PathHelper.trimSlashes(currentContent.Path)) {
                                return requestReload();
                            }
                            break;
                        default:
                            requestReload();
                    }
                }
            }),
            eventHub.onContentCreated.subscribe((created) => handleCreate([created.content])),
            eventHub.onContentCopied.subscribe((copied) => handleCreate(copied.content)),
            eventHub.onContentMoved.subscribe((moved) => {
                if (moved.content.some((content) => alwaysRefresh ||
                    client_utils_1.PathHelper.isAncestorOf(currentContent.Path, content.Path) ||
                    client_utils_1.PathHelper.getParentPath(content.OriginalPath) === client_utils_1.PathHelper.trimSlashes(currentContent.Path))) {
                    requestReload();
                }
            }),
            eventHub.onContentModified.subscribe((mod) => {
                if (alwaysRefresh || mod.forceRefresh || children.some((c) => c.Id === mod.content.Id)) {
                    requestReload();
                }
            }),
            eventHub.onUploadFinished.subscribe((data) => {
                if (alwaysRefresh || client_utils_1.PathHelper.isAncestorOf(currentContent.Path, data.Url)) {
                    requestReload();
                }
            }),
            eventHub.onContentDeleted.subscribe((del) => {
                if (alwaysRefresh ||
                    del.contentData.some((deletedContent) => client_utils_1.PathHelper.getParentPath(deletedContent.Path) === client_utils_1.PathHelper.trimSlashes(currentContent.Path))) {
                    requestReload();
                }
            }),
        ];
        return () => subscriptions.forEach((s) => s.dispose());
    }, [
        currentContent,
        repo,
        children,
        eventHub.onCustomActionExecuted,
        eventHub.onContentCreated,
        eventHub.onContentCopied,
        eventHub.onContentMoved,
        eventHub.onContentModified,
        eventHub.onContentDeleted,
        eventHub.onUploadFinished,
        alwaysRefresh,
    ]);
    if (error) {
        throw error;
    }
    return react_1.default.createElement(exports.CurrentChildrenContext.Provider, { value: children }, props.children);
};
exports.CurrentChildrenProvider = CurrentChildrenProvider;
//# sourceMappingURL=current-children.js.map